<template>
  <div class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Overlay -->
    <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"></div>

    <!-- Modal Container -->
    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative transform overflow-hidden rounded-lg bg-gray-800 text-white shadow-xl transition-all sm:max-w-3xl w-full">
          <div class="p-6">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
              <h3 class="text-xl font-semibold text-indigo-300">สร้างวิญญาณใหม่</h3>
              <button @click="cancelModal" class="text-gray-400 hover:text-white">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Tabs Navigation -->
            <div class="flex space-x-4 mb-6">
              <button
                @click="currentTab = 'basic'"
                :class="{
                  'px-4 py-2 rounded-lg text-white': true,
                  'bg-indigo-600 hover:bg-indigo-700': currentTab === 'basic',
                  'bg-gray-600 hover:bg-gray-700': currentTab !== 'basic'
                }"
              >
                ข้อมูลพื้นฐาน
              </button>
              <button
                v-if="ghostType === 'boss'"
                @click="currentTab = 'drops'"
                :class="{
                  'px-4 py-2 rounded-lg text-white': true,
                  'bg-indigo-600 hover:bg-indigo-700': currentTab === 'drops',
                  'bg-gray-600 hover:bg-gray-700': currentTab !== 'drops'
                }"
              >
                ไอเทมที่ดรอป
              </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="createGhost">
              <!-- Basic Info and Stats Section -->
              <div v-if="currentTab === 'basic'" class="space-y-4 mb-6">
                <h4 class="text-lg font-medium text-gray-300">ข้อมูลพื้นฐาน</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">ชื่อวิญญาณ</label>
                    <input
                      v-model="ghostName"
                      type="text"
                      placeholder="เช่น วิญญาณธรรมดา"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">ลิงก์รูปภาพ</label>
                    <input
                      v-model="imageUrl"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">เลเวลของวิญญาณ</label>
                    <input
                      v-model.number="level"
                      type="number"
                      min="1"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">ประเภทวิญญาณ</label>
                    <select
                      v-model="ghostType"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    >
                      <option value="normal">ทั่วไป</option>
                      <option value="boss">บอส</option>
                    </select>
                  </div>
                </div>

                <!-- Stats Section -->
                <h4 class="text-lg font-medium text-gray-300 mt-6">ค่าสถานะ</h4>
                <div class="grid grid-cols-2 sm:grid-cols-6 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Stamina</label>
                    <input
                      v-model.number="stats.stamina"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Strength</label>
                    <input
                      v-model.number="stats.str"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Agility</label>
                    <input
                      v-model.number="stats.agi"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Intelligence</label>
                    <input
                      v-model.number="stats.int"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Defense</label>
                    <input
                      v-model.number="stats.defense"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Luck</label>
                    <input
                      v-model.number="stats.luck"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <!-- Drops Section (only for boss) -->
              <div v-if="currentTab === 'drops' && ghostType === 'boss'" class="space-y-4 mb-6">
                <h4 class="text-lg font-medium text-gray-300">ไอเทมที่ดรอป</h4>
                <div v-for="(drop, index) in drops" :key="index" class="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4 items-center">
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">ชื่อไอเทม</label>
                    <input
                      v-model="drop.itemName"
                      type="text"
                      placeholder="ชื่อไอเทม"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">โอกาสดรอป (1-10000)</label>
                    <input
                      v-model.number="drop.dropChance"
                      type="number"
                      min="1"
                      max="10000"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div class="flex items-center">
                    <label class="block text-sm font-medium text-gray-300">
                      <input type="checkbox" v-model="drop.transferable" class="mr-2" />
                      สามารถโอนได้
                    </label>
                  </div>
                  <div class="flex items-end">
                    <button
                      @click="removeDrop(index)"
                      type="button"
                      class="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
                <button
                  @click="addDrop"
                  type="button"
                  class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-200"
                >
                  เพิ่มไอเทม
                </button>
              </div>

              <!-- Action Buttons -->
              <div class="flex justify-end gap-3">
                <button
                  type="submit"
                  class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200"
                >
                  สร้างวิญญาณ
                </button>
                <button
                  @click="cancelModal"
                  type="button"
                  class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config';
import { ref, watch } from 'vue';

export default {
  setup(props, context) {
    const ghostName = ref('');
    const imageUrl = ref('');
    const level = ref(1);
    const ghostType = ref('normal');
    const stats = ref({
      stamina: 0,
      str: 0,
      agi: 0,
      int: 0,
      defense: 0,
      luck: 0,
    });
    const drops = ref([]);
    const itemNames = ref([]);
    const currentTab = ref('basic');
    const now = new Date();

    // Watch for changes in ghostType to manage drops tab and itemNames
    watch(ghostType, () => {
      if (ghostType.value !== 'boss') {
        drops.value = [];
        itemNames.value = [];
        if (currentTab.value === 'drops') {
          currentTab.value = 'basic';
        }
      }
    });

    // Watch for changes in drops to update itemNames
    watch(drops, (newDrops) => {
      itemNames.value = newDrops.map(drop => drop.itemName);
    }, { deep: true });

    const addDrop = () => {
      drops.value.push({
        itemName: '',
        dropChance: 1,
        transferable: false,
      });
    };

    const removeDrop = (index) => {
      drops.value.splice(index, 1);
    };

    const cancelModal = () => {
      context.emit('cancelModal');
    };

    const createGhost = async () => {
      try {
        const ghostData = {
          name: ghostName.value,
          imageUrl: imageUrl.value,
          level: level.value,
          type: ghostType.value,
          stats: {
            stamina: stats.value.stamina,
            str: stats.value.str,
            agi: stats.value.agi,
            int: stats.value.int,
            defense: stats.value.defense,
            luck: stats.value.luck,
          },
          createdAt: now,
          damageTaken: 0,
          itemNames: itemNames.value,
        };

        if (ghostType.value === 'boss') {
          ghostData.drops = drops.value;
        }

        await projectFirestore.collection('gameGhosts').doc(ghostName.value).set(ghostData);
        context.emit('ghostAdded');
        
        // Reset form
        ghostName.value = '';
        imageUrl.value = '';
        level.value = 1;
        ghostType.value = 'normal';
        stats.value = { stamina: 0, str: 0, agi: 0, int: 0, defense: 0, luck: 0 };
        drops.value = [];
        itemNames.value = [];
        currentTab.value = 'basic';
      } catch (error) {
        console.error('Error adding ghost:', error);
      }
    };

    return { 
      ghostName,
      imageUrl,
      level,
      ghostType,
      stats,
      drops,
      itemNames,
      currentTab,
      addDrop,
      removeDrop,
      createGhost,
      cancelModal,
    };
  },
};
</script>